//! Excel's page-break editing rules, kept free of pixi and worker calls so they
//! can be unit tested.
//!
//! Breaks are absolute 1-based track indices: a break at `n` means page content
//! resumes at track `n`, matching Excel and the xlsx `<rowBreaks>` element.

/** Excel's scaling floor and ceiling for "Adjust to N%". */
const MIN_SCALE_PERCENT = 10;
const MAX_SCALE_PERCENT = 400;

export interface BreakDrag {
  /** Manual breaks on the dragged axis today. */
  breaks: number[];
  /** The track the dragged line currently starts a page at. */
  from: number;
  /** The track the user dropped it on. */
  to: number;
  /** First track of the page being resized (the band left of the line). */
  bandStart: number;
  /** Last track of the printed region. */
  regionEnd: number;
}

/**
 * The manual breaks after a drag, plus where the resized page now ends
 * (exclusive), following Excel:
 * - dragging an automatic break makes it manual at the drop position,
 * - dragging past the end of the region removes the break, leaving the breaks
 *   below it alone,
 * - a page can never be emptied, so the drop is clamped past the band's start,
 * - breaks the page swallowed on the way are dropped.
 */
export const applyBreakDrag = ({
  breaks,
  from,
  to,
  bandStart,
  regionEnd,
}: BreakDrag): { breaks: number[]; bandEnd: number } => {
  const target = Math.max(bandStart + 1, to);
  const sort = (values: number[]) => values.sort((a, b) => a - b);

  if (target > regionEnd) {
    const remaining = sort(breaks.filter((value) => value !== from));
    const next = remaining.find((value) => value > bandStart);
    return { breaks: remaining, bandEnd: next ?? regionEnd + 1 };
  }

  const remaining = breaks.filter((value) => value !== from && !(value > bandStart && value < target));
  return { breaks: sort([...remaining, target]), bandEnd: target };
};

/**
 * The scale a page needs to hold `bandPx` of content, as a percentage, or
 * undefined when it already fits. Uses the paginator's fit rule — a band fits
 * when `size <= content / scale - title` — so a break dragged outward is
 * honored by shrinking, which is what Excel does rather than ignoring the drag.
 *
 * The result stays inside Excel's 10–400% range for "Adjust to N%". A sheet
 * already scaled above 100% keeps whatever intermediate scale it needs, so the
 * drag doesn't shrink every page down to 100%.
 */
export const requiredScalePercent = ({
  bandPx,
  titlePx,
  contentPx,
  currentScale,
}: {
  bandPx: number;
  titlePx: number;
  contentPx: number;
  currentScale: number;
}): number | undefined => {
  if (bandPx <= 0 || contentPx <= 0) return undefined;
  if (bandPx <= contentPx / currentScale - titlePx + 1e-6) return undefined;

  const required = contentPx / (bandPx + titlePx);
  const percent = Math.max(MIN_SCALE_PERCENT, Math.min(MAX_SCALE_PERCENT, Math.floor(required * 100)));
  return percent;
};

/** The edges of the print area that can be dragged, in A1 terms. */
export type PrintAreaEdge = 'left' | 'right' | 'top' | 'bottom';

export interface PrintAreaRect {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/**
 * The print area after one of its edges is dropped on `target` (a 1-based
 * boundary, so the right and bottom edges sit one track past the last printed
 * one). The edges that weren't dragged keep the area's own bounds — print
 * titles extend the shaded region but are not part of the area, so resizing
 * must not absorb them. An edge can't cross its opposite, which would invert
 * the area.
 */
export const resizePrintArea = (area: PrintAreaRect, edge: PrintAreaEdge, target: number): PrintAreaRect => {
  switch (edge) {
    case 'left':
      return { ...area, minX: Math.min(target, area.maxX) };
    case 'right':
      return { ...area, maxX: Math.max(target - 1, area.minX) };
    case 'top':
      return { ...area, minY: Math.min(target, area.maxY) };
    case 'bottom':
      return { ...area, maxY: Math.max(target - 1, area.minY) };
  }
};

/**
 * Where a break line lands when dropped at `position`: the boundary of the
 * nearest track edge, so a line never falls inside a track. Mirrors the
 * freeze-divider drag.
 */
export const snapToTrack = ({
  position,
  trackStart,
  trackSize,
  hovered,
}: {
  position: number;
  trackStart: number;
  trackSize: number;
  hovered: number;
}): number => (position >= trackStart + trackSize / 2 ? hovered + 1 : hovered);
